import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function SignInDialog({ open, onOpenChange }) {
  const { login: googleLogin, refresh } = useAuth();
  const [providers, setProviders] = useState({});
  useEffect(() => {
    api.get("/auth/oauth/providers").then(({ data }) => setProviders(data || {})).catch(() => setProviders({}));
  }, []);

  const oauthLogin = (provider) => {
    window.location.href = `${API}/auth/oauth/${provider}/login`;
  };

  const passkeyLogin = async () => {
    const identifier = prompt("Enter your email or phone (with +country code):");
    if (!identifier) return;
    try {
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const payload = identifier.includes("@") ? { email: identifier } : { phone: identifier };
      const { data: options } = await api.post("/auth/passkeys/authenticate/begin", payload);
      const cred = await startAuthentication({ optionsJSON: options });
      const { data } = await api.post("/auth/passkeys/authenticate/finish", { challenge_id: options.challenge_id, credential: cred });
      toast.success(`Signed in as ${data.name || data.email}`);
      await refresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Passkey login failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[color:var(--bg-surface)] border border-[color:var(--border-default)] text-white sm:max-w-[480px] max-h-[90vh] overflow-y-auto" data-testid="signin-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-elite text-3xl tracking-tight">Sign in</DialogTitle>
          <DialogDescription className="text-[color:var(--text-secondary)] text-xs font-mono-data tracking-wider">
            INSTITUTIONAL ACCESS · MULTIPLE METHODS
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="social" className="mt-2">
          <TabsList className="grid grid-cols-4 bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] rounded-none p-0 h-auto">
            <TabsTrigger value="social" data-testid="tab-social" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-data text-[10px] tracking-widest py-2">SOCIAL</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-data text-[10px] tracking-widest py-2">EMAIL</TabsTrigger>
            <TabsTrigger value="mobile" data-testid="tab-mobile" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-data text-[10px] tracking-widest py-2">MOBILE</TabsTrigger>
            <TabsTrigger value="passkey" data-testid="tab-passkey" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono-data text-[10px] tracking-widest py-2">PASSKEY</TabsTrigger>
          </TabsList>

          <TabsContent value="social" className="mt-4 space-y-2">
            <SocialBtn onClick={googleLogin} testid="dialog-google-btn" label="CONTINUE WITH GOOGLE" primary />
            <SocialBtn onClick={() => oauthLogin("apple")} testid="dialog-apple-btn" label="APPLE" configured={providers.apple?.configured} />
            <SocialBtn onClick={() => oauthLogin("microsoft")} testid="dialog-microsoft-btn" label="MICROSOFT" configured={providers.microsoft?.configured} />
            <SocialBtn onClick={() => oauthLogin("github")} testid="dialog-github-btn" label="GITHUB" configured={providers.github?.configured} />
            <SocialBtn onClick={() => oauthLogin("linkedin")} testid="dialog-linkedin-btn" label="LINKEDIN" configured={providers.linkedin?.configured} />
            {Object.values(providers).some((p) => !p.configured) && (
              <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-2 text-center leading-relaxed">
                Grayed-out providers need admin to add client credentials to backend/.env.
              </div>
            )}
          </TabsContent>

          <TabsContent value="email" className="mt-4">
            <OtpFlow kind="email" placeholder="you@company.com" validator={(v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)}
              validationError="Enter a valid email address" sendPath="/auth/email/send-otp" verifyPath="/auth/email/verify-otp"
              payloadKey="email" label="EMAIL" onDone={() => onOpenChange(false)} />
          </TabsContent>

          <TabsContent value="mobile" className="mt-4">
            <OtpFlow kind="mobile" placeholder="+14155552671" validator={(v) => /^\+[1-9]\d{6,14}$/.test(v)}
              validationError="Enter phone in E.164 format (e.g. +14155552671)" sendPath="/auth/mobile/send-otp" verifyPath="/auth/mobile/verify-otp"
              payloadKey="phone" label="PHONE (E.164)" defaultValue="+" onDone={() => onOpenChange(false)} />
          </TabsContent>

          <TabsContent value="passkey" className="mt-4 space-y-2">
            <div className="text-xs text-[color:var(--text-secondary)] font-body">Password-less login with Face ID, Touch ID, Windows Hello, or a security key.</div>
            <button data-testid="passkey-login-btn" onClick={passkeyLogin} className="w-full bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 transition-colors">
              ◈ SIGN IN WITH PASSKEY
            </button>
            <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] mt-2 text-center">
              First-time? Sign in with email/phone once, then register a passkey in Security.
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function SocialBtn({ onClick, testid, label, configured = true, primary = false }) {
  const disabled = !configured && !primary;
  return (
    <button
      data-testid={testid}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full py-2.5 text-xs font-mono-data tracking-widest transition-colors flex items-center justify-center gap-2 ${
        primary ? "bg-white text-black hover:opacity-90" :
        configured ? "border border-[color:var(--border-default)] text-white hover:bg-[color:var(--bg-hover)]" :
        "border border-[color:var(--border-default)] text-[#52525B] cursor-not-allowed"
      }`}
    >{label}{!configured && !primary ? " · NOT CONFIGURED" : ""}</button>
  );
}

function OtpFlow({ kind, placeholder, validator, validationError, sendPath, verifyPath, payloadKey, label, defaultValue = "", onDone }) {
  const { refresh } = useAuth();
  const [identifier, setIdentifier] = useState(defaultValue);
  const [code, setCode] = useState("");
  const [step, setStep] = useState("id");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pending2fa, setPending2fa] = useState(null); // {challenge_id, expires_in}
  const [totpCode, setTotpCode] = useState("");

  const send = async () => {
    if (!validator(identifier)) { toast.error(validationError); return; }
    setSending(true);
    try {
      const { data } = await api.post(sendPath, { [payloadKey]: identifier });
      setStep("code");
      if (data.mock) toast.info("Check server logs for the code (dev mode)");
      else toast.success("Code sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const verify = async () => {
    if (!/^\d{4,8}$/.test(code)) { toast.error("Enter the code"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post(verifyPath, { [payloadKey]: identifier, code });
      if (data.pending_2fa && data.challenge_id) {
        setPending2fa({ challenge_id: data.challenge_id, expires_in: data.expires_in || 300 });
        setStep("totp");
        toast.info("2FA required — enter your authenticator code");
      } else {
        await refresh();
        toast.success(`Signed in as ${data.name || data.email || data.phone}`);
        onDone?.();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setVerifying(false); }
  };

  const complete2fa = async () => {
    if (!/^\d{6}$/.test(totpCode)) { toast.error("Enter 6-digit authenticator code"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/auth/2fa/complete", { challenge_id: pending2fa.challenge_id, code: totpCode });
      await refresh();
      toast.success(`Signed in as ${data.name || data.email || data.phone}`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "2FA verification failed"); }
    finally { setVerifying(false); }
  };

  if (step === "id") {
    return (
      <>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">{label}</div>
        <input data-testid={`${kind}-id-input`} value={identifier} onChange={(e) => setIdentifier(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={placeholder} className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2.5 text-sm font-mono-data text-white focus:outline-none focus:border-[color:var(--border-focus)]" />
        <button data-testid={`${kind}-send-btn`} onClick={send} disabled={sending} className="w-full mt-3 bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors">{sending ? "SENDING…" : "SEND CODE →"}</button>
      </>
    );
  }
  if (step === "totp") {
    return (
      <>
        <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">◈ 2FA · AUTHENTICATOR CODE</div>
        <input data-testid={`${kind}-totp-input`} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} onKeyDown={(e) => e.key === "Enter" && complete2fa()} placeholder="6-digit TOTP" className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2.5 text-lg font-mono-data text-white tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]" autoFocus />
        <button data-testid={`${kind}-totp-verify-btn`} onClick={complete2fa} disabled={verifying} className="w-full mt-3 bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors">{verifying ? "VERIFYING…" : "COMPLETE SIGN-IN →"}</button>
      </>
    );
  }
  return (
    <>
      <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest mb-1">CODE SENT TO {identifier}</div>
      <input data-testid={`${kind}-code-input`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))} onKeyDown={(e) => e.key === "Enter" && verify()} placeholder="6-digit code" className="w-full bg-[color:var(--bg-elevated)] border border-[color:var(--border-default)] px-3 py-2.5 text-lg font-mono-data text-white tracking-widest focus:outline-none focus:border-[color:var(--border-focus)]" autoFocus />
      <button data-testid={`${kind}-verify-btn`} onClick={verify} disabled={verifying} className="w-full mt-3 bg-white text-black py-2.5 text-xs font-mono-data tracking-widest hover:opacity-90 disabled:opacity-50 transition-colors">{verifying ? "VERIFYING…" : "VERIFY & SIGN IN →"}</button>
      <button data-testid={`${kind}-back-btn`} onClick={() => { setStep("id"); setCode(""); }} className="w-full mt-2 border border-[color:var(--border-default)] py-2 text-[11px] font-mono-data text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-hover)] transition-colors">← BACK</button>
    </>
  );
}
